// A maintenance option's "Maintenance Agreement Set Up" (#515; Take Off R2:Z11) and its Overall
// Summary (Z22:Z31), beside the Take Off. The years and tier are the option's own columns; the rest
// is hig_scope.maintenance. Blank = the default, shown greyed (the option's labour type, the CRM
// setting, 0 %, No, the pricing date's year).
import type { ReactNode } from 'react';
import { LABOUR_TYPES, MAINTENANCE_TIERS, MAINTENANCE_YEARS, PAYMENT_TYPES, setMaintenance, type MaintenanceChoice, type OptionScope } from '@higgins/m2q-pricing';
import type { Fields } from '../lib/device-db';
import { IN_FIELD_ORDER, moveToNextField } from '../lib/field-order';
import type { MaintenancePricing } from '../lib/maintenance-pricing';
import { NumberField } from './number-field';
import { Block, CostSummary, type CostFigures } from './rates-panel';
import { formatTwo } from './surface-format';
import { CHANGED } from './take-off-rows';

const SELECT = 'h-7 rounded border border-slate-300 px-1 text-sm';
const PERCENT = 100;
const YES_NO = { yes: 'Yes', no: 'No' } as const;

function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <tr>
      <td className="py-0.5 pr-2 text-slate-600">{label}</td>
      <td className="py-0.5 text-right">{children}</td>
    </tr>
  );
}

interface Props {
  pricing: MaintenancePricing;
  choice: MaintenanceChoice | undefined;
  /** The option's labour type: the servicing rate's default. */
  labourType: string;
  /** The setting's 2nd repaint coats, for the placeholder. */
  defaultCoats: number;
  defaultStartYear: number;
  priceShown: boolean;
  onScope: (change: (scope: OptionScope) => OptionScope) => void | Promise<void>;
  onFields: (fields: Fields) => void | Promise<void>;
}

export function MaintenanceSetupBox({ pricing, choice, labourType, defaultCoats, defaultStartYear, onScope, onFields }: Omit<Props, 'priceShown'>) {
  const set = (change: Partial<Record<keyof MaintenanceChoice, unknown>>) => onScope((s) => setMaintenance(s, change));
  const { setup } = pricing;
  // One half of the additional $/hr; clearing both drops it.
  const setExtraRate = (half: 'sell' | 'cost', value: number | null) => {
    const next = { ...choice?.extraRate, [half]: value ?? undefined };
    set({ extraRate: next.sell === undefined && next.cost === undefined ? null : next });
  };
  const yesNo = (value: boolean | undefined, key: 'includeAccess' | 'includePrelims', label: string) => (
    <select {...IN_FIELD_ORDER} onKeyDown={(e) => moveToNextField(e)} value={value ? YES_NO.yes : YES_NO.no} onChange={(e) => set({ [key]: e.target.value === YES_NO.yes || null })} aria-label={label} className={`${SELECT} ${value ? CHANGED : 'bg-white'}`}>
      <option>{YES_NO.no}</option>
      <option>{YES_NO.yes}</option>
    </select>
  );
  return (
    <Block title="Maintenance Agreement Set Up">
      <table className="w-full">
        <tbody>
          <Row label="Servicing rate">
            <select
              {...IN_FIELD_ORDER}
              onKeyDown={(e) => moveToNextField(e)}
              value={choice?.servicingLabourType ?? ''}
              onChange={(e) => set({ servicingLabourType: e.target.value || null })}
              aria-label="Servicing labour type"
              className={`${SELECT} ${choice?.servicingLabourType ? CHANGED : 'bg-white'}`}
            >
              <option value="">{labourType} (the option&rsquo;s)</option>
              {LABOUR_TYPES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
            <span className="ml-1 text-xs tabular-nums text-slate-500">
              {formatTwo(setup.servicing.sell)} / {formatTwo(setup.servicing.cost)}
            </span>
          </Row>
          <Row label="Service additional rate $/hr, sell · cost">
            <span className="inline-flex gap-1">
              <NumberField compact value={choice?.extraRate?.sell} placeholder="0" onChange={(v) => setExtraRate('sell', v)} label="Service additional rate sell $/hr" className="w-14" />
              <NumberField compact value={choice?.extraRate?.cost} placeholder="0" onChange={(v) => setExtraRate('cost', v)} label="Service additional rate cost $/hr" className="w-14" />
            </span>
          </Row>
          <Row label={`Agreement years (${MAINTENANCE_YEARS.min}–${MAINTENANCE_YEARS.max})`}>
            <NumberField compact value={pricing.years} onChange={(v) => onFields({ hig_termyears: v === null ? null : Math.round(v) })} label="Agreement years" className="w-14" />
          </Row>
          <Row label="Service years">
            <span className="tabular-nums">{pricing.years - 1}</span>
          </Row>
          <Row label="2nd repaint coats">
            <NumberField compact value={choice?.secondRepaintCoats} placeholder={String(defaultCoats)} onChange={(v) => set({ secondRepaintCoats: v })} label="Second repaint coats" className="w-14" />
          </Row>
          <Row label="Extra servicing % (each surface starts with it)">
            <span>
              <NumberField compact value={choice?.extraServicing} placeholder="0" onChange={(v) => set({ extraServicing: v })} label="Extra servicing %" className="w-14" />%
            </span>
          </Row>
          <Row label="Include access servicing above 3 m">{yesNo(choice?.includeAccess, 'includeAccess', 'Include access above 3 m')}</Row>
          <Row label="Include prelims in servicing">{yesNo(choice?.includePrelims, 'includePrelims', 'Include prelims in servicing')}</Row>
          <Row label="Maintenance tier">
            <select {...IN_FIELD_ORDER} onKeyDown={(e) => moveToNextField(e)} value={pricing.tier ?? ''} onChange={(e) => onFields({ hig_maintenancetier: e.target.value || null })} aria-label="Maintenance tier" className={`${SELECT} bg-white`}>
              <option value="">—</option>
              {MAINTENANCE_TIERS.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </Row>
          <Row label="Payment type">
            <select
              {...IN_FIELD_ORDER}
              onKeyDown={(e) => moveToNextField(e)}
              value={choice?.paymentType ?? 'PAYG'}
              onChange={(e) => set({ paymentType: e.target.value === 'PAYG' ? null : e.target.value })}
              aria-label="Payment type"
              className={`${SELECT} ${choice?.paymentType ? CHANGED : 'bg-white'}`}
            >
              {PAYMENT_TYPES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
            {setup.payment && setup.payment.type !== 'PAYG' && <span className="ml-1 text-xs text-slate-500">at {formatTwo(setup.payment.financeRate * PERCENT)}% finance</span>}
          </Row>
          <Row label="Start year">
            <NumberField compact value={choice?.startYear} placeholder={String(defaultStartYear)} onChange={(v) => set({ startYear: v === null ? null : Math.round(v) })} label="Start year" className="w-16" />
          </Row>
        </tbody>
      </table>
      <p className="mt-2 text-xs text-slate-500">Each area starts on year 1 Repaint, then Service; change any year under the area.</p>
    </Block>
  );
}

/** The contract over every year (Overall Summary): the cost by category, hours and litres, sell and GP. */
export function OverallSummary({ pricing, priceShown }: { pricing: MaintenancePricing; priceShown: boolean }) {
  const { yearly } = pricing;
  const costs: CostFigures['costs'] = [yearly.totals.labour.cost, yearly.totals.materials.cost, yearly.totals.hire.cost, yearly.totals.prelims.cost];
  return <CostSummary title={`Overall Summary (${pricing.years} years)`} hoursLabel="Overall Hrs · Lts" figures={{ ...yearly, costs }} priceShown={priceShown} />;
}
